/**
 * ArenaScene
 *
 * Hosts the live multiplayer view. Networking, input, and rendering live
 * in `ArenaController`; this class is the thin Phaser shell that drives
 * the controller's lifecycle.
 *
 * Stage 4/5 architecture: server-authoritative. Client sends `ShipInput`,
 * consumes `GameState` / `PlayerJoined` / `PlayerLeft` events. Stage 5
 * adds local prediction + reconciliation for own ship (see
 * `ArenaController`). `update(time, delta)` forwards Phaser's fixed-step
 * timing to drive the prediction physics tick.
 *
 * TODO(Stage 6): Hermite-spline interpolation instead of linear lerp for
 *   remote ships.
 * TODO(Stage 7): DataSubscription for live MatchConfig / Player.Stats
 *   instead of one-shot DataGet at boot.
 */

import Phaser from 'phaser';

import { getClient } from '../playserv';
import { ArenaController } from './arena/ArenaController';
import { EntryOverlay } from '../ui/EntryOverlay';
import { generateCallsign, readPilotName, writePilotName } from '../identity';
import { formatCountdown, loadSeasonInfo } from '../data/seasons';

export class ArenaScene extends Phaser.Scene {
  private controller: ArenaController | null = null;

  constructor() {
    super({ key: 'Arena' });
  }

  async create(): Promise<void> {
    const client = getClient();
    this.controller = new ArenaController(client, this);
    // PSV-1726 — dev-only debug hook for automated browser tests
    // (Playwright reads predicted/remote ship state through it).
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__arenaDebug = this.controller;
    }

    // PSV-1890 — entry screen: the arena joins only after PLAY. The
    // starfield renders behind the DOM overlay meanwhile; one click (or
    // Enter) from load to flying. Every human tab gets the screen —
    // including `?pilot=` QA tabs (each keeps its own per-tab name);
    // only explicit `?skipEntry=1` bypasses it (browser automation).
    const skipEntry =
      new URLSearchParams(window.location.search).get('skipEntry') === '1';
    if (!skipEntry) {
      const chosenName = await new Promise<string>((resolve) => {
        // PSV-1906 — season line loads async; the overlay shows instantly.
        void loadSeasonInfo(getClient()).then((info) => {
          if (info === null) return;
          const parts = [
            `SEASON ${info.season} · ends in ${formatCountdown(info.endsAt, Date.now())}`,
          ];
          if (info.leaderLine !== null) parts.push(info.leaderLine);
          if (info.lastChampionLine !== null) parts.push(info.lastChampionLine);
          overlay.setSeasonLine(parts.join('  ·  '));
        });
        const overlay = new EntryOverlay({
          // PSV-1902 UX — suggest a system callsign when the player has
          // no stored name: PLAY works immediately, typing replaces it
          // (the input is select()'ed on show, so typing overwrites).
          initialName: readPilotName() ?? generateCallsign(),
          onPlay: (name) => {
            overlay.destroy();
            resolve(name);
          },
        });
        overlay.show();
      });
      writePilotName(chosenName);
      this.controller.setPilotName(chosenName);
    }

    try {
      await this.controller.start();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.add.text(20, 20, `Failed to join arena: ${message}`, {
        fontSize: '13px',
        color: '#ff6b6b',
      });
      // eslint-disable-next-line no-console
      console.error('[arena] controller.start() failed:', err);
      this.controller = null;
      return;
    }

    // Phaser tears scenes down via the 'shutdown' event; controller cleanup
    // is fire-and-forget (unsubscribe RPC may still be in flight).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      void this.controller?.stop();
      this.controller = null;
    });
  }

  override update(time: number, delta: number): void {
    this.controller?.update(time, delta);
  }
}
